import React, { useState, useEffect } from 'react'
import { storage } from './firebaseConfig'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { getAllAssets, addAsset } from '../../services/assetService'
import withAdminAuth from './withAdminAuth'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'

const CATEGORY_CONFIG = {
  samples: {
    prefix: 'diy_sample',
    folder: 'live_templates/samples',
    label: 'Samples'
  },
  backgrounds: {
    prefix: 'diy_background',
    folder: 'live_templates/backgrounds',
    label: 'Backgrounds'
  },
  decorations: {
    prefix: 'diy_decoration',
    folder: 'live_templates/decorations',
    label: 'Decorations'
  },
  frames: {
    prefix: 'diy_frame',
    folder: 'live_templates/frames',
    label: 'Frames'
  }
}

function getNextNumber (assets, category) {
  const items = assets[category] || []
  if (items.length === 0) return 1
  const nums = items.map(item => {
    const match = item.id.match(/(\d+)$/)
    return match ? parseInt(match[1], 10) : 0
  })
  return Math.max(...nums) + 1
}

function padNumber (n) {
  return String(n).padStart(4, '0')
}

function UploadImageSystem () {
  const [category, setCategory] = useState('samples')
  const [assets, setAssets] = useState(null)
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [status, setStatus] = useState('')
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [uploading, setUploading] = useState(false)

  const fetchAssets = async () => {
    const data = await getAllAssets()
    setAssets(data)
  }

  useEffect(() => {
    fetchAssets()
  }, [])

  const handleFileChange = (e) => {
    const selected = e.target.files[0]
    if (!selected) return
    setFile(selected)
    setPreview(URL.createObjectURL(selected))
    setStatus('')
    setUploadSuccess(false)
  }

  const handleCategoryChange = (e) => {
    setCategory(e.target.value)
    setFile(null)
    setPreview(null)
    setStatus('')
    setUploadSuccess(false)
  }

  const getNextFilename = () => {
    if (!assets) return null
    const config = CATEGORY_CONFIG[category]
    const nextNum = getNextNumber(assets, category)
    const ext = file ? file.name.split('.').pop() : '?'
    return `${config.prefix}_${padNumber(nextNum)}.${ext}`
  }

  const handleUpload = async () => {
    if (!file) {
      setStatus('Please select a file first.')
      return
    }
    if (!assets) {
      setStatus('Assets not loaded yet. Please wait.')
      return
    }

    const config = CATEGORY_CONFIG[category]
    const nextNum = getNextNumber(assets, category)
    const ext = file.name.split('.').pop()
    const newId = `${config.prefix}_${padNumber(nextNum)}`
    const newFilename = `${newId}.${ext}`
    const storagePath = `${config.folder}/${newFilename}`

    setUploading(true)
    setStatus(`Uploading ${newFilename} to Firebase...`)

    try {
      const storageRef = ref(storage, storagePath)
      const snapshot = await uploadBytes(storageRef, file)
      const url = await getDownloadURL(snapshot.ref)

      setStatus('Saving to database...')
      await addAsset(category, newId, url, storagePath, ext)

      setStatus(`Done! Uploaded: ${newFilename}`)
      setUploadSuccess(true)
      setFile(null)
      setPreview(null)

      await fetchAssets()
    } catch (err) {
      console.error(err)
      setStatus(`Error: ${err.message}`)
      setUploadSuccess(false)
    } finally {
      setUploading(false)
    }
  }

  const nextFilename = getNextFilename()

  return (
    <>
      <div style={{ width: '100%', height: '2.7rem' }} />
      <div style={{ display: 'flex', justifyContent: 'center', color: 'aliceblue', padding: '1rem' }}>
        <div style={{ width: 'min(100%, 480px)', display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
          <h2 style={{ fontFamily: 'Philosopher, sans-serif', textAlign: 'center' }}>Upload Image System</h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <label style={{ fontWeight: 'bold' }}>Category</label>
            <select
              value={category}
              onChange={handleCategoryChange}
              style={{ padding: '0.5rem', borderRadius: '4px', fontSize: '1rem' }}
            >
              {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>
          </div>

          {assets && (
            <div style={{ color: '#aef', fontSize: '0.9rem' }}>
              Next filename: <strong>{nextFilename || `${CATEGORY_CONFIG[category].prefix}_XXXX.ext`}</strong>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <label style={{ fontWeight: 'bold' }}>Select Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ color: 'aliceblue' }}
            />
          </div>

          {preview && (
            <div>
              <img
                src={preview}
                alt="Preview"
                style={{ maxWidth: '100%', maxHeight: '240px', borderRadius: '8px', border: '1px solid #56caf5' }}
              />
            </div>
          )}

          {status && (
            <Alert severity={uploadSuccess ? 'success' : 'info'}>
              <AlertTitle>{uploadSuccess ? 'Success' : 'Status'}</AlertTitle>
              {status}
            </Alert>
          )}

          <button
            onClick={handleUpload}
            disabled={uploading || !file}
            style={{
              padding: '0.75rem',
              backgroundColor: uploading || !file ? '#444' : '#56caf5',
              color: '#000',
              border: 'none',
              borderRadius: '6px',
              fontSize: '1rem',
              cursor: uploading || !file ? 'not-allowed' : 'pointer',
              fontWeight: 'bold'
            }}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </>
  )
}

export default withAdminAuth(UploadImageSystem)
